/**
 * "You've arrived at OZORA" (plan.md step 60 task 4, the user, 2026-10-16:
 * "sounds good, build it"). The phone watches the places of the raves you
 * ATTEND — a geofence, in the background, with the "Always" location
 * permission the person turns on themselves — and when it enters one during
 * the rave, it says so ONCE: the rave is ready, the lineup and map work with
 * no signal. The position never leaves the phone; the page hands the shell
 * only the raves' own places, and the shell decides here.
 */

/** One rave the phone watches for. `id` is the edition; `url` opens it. */
export interface ArrivalRegion {
  id: string;
  name: string;
  lat: number;
  lng: number;
  radiusM: number;
  /** ISO: watched from two days before the gates to the end. */
  from: string;
  to: string;
  url: string;
  /** The notice's words in the reader's language, from the page (step 73) — the shell holds no translations. */
  title?: string;
  body?: string;
}

/** How near the rave's point counts as there — a festival site is big. */
export const ARRIVAL_RADIUS_M = 1500;
/** The OS watches at most 20 places (iOS's limit). */
export const MAX_ARRIVAL_REGIONS = 20;
const DAY_MS = 24 * 60 * 60_000;

export interface ArrivalSource {
  editionId: string; editionName: string; editionSlug: string; subdomain: string;
  gatesOpenAt: string | null; endsAt: string | null;
  lat?: number | null; lng?: number | null; attending?: boolean;
}

/** The watch starts this long before the gates (the user, 2026-10-16: "only on the day of the rave or 2 before"). */
export const ARRIVAL_LEAD_MS = 2 * DAY_MS;

/**
 * The raves worth watching NOW: attended, with a place and dates, from two
 * days before the gates to the end — the switch stays on all year, the phone
 * watches only then —
 * soonest first, at most `MAX_ARRIVAL_REGIONS`. `urlOf` builds the rave's
 * address (the page knows the platform domain).
 */
export function arrivalRegions(
  raves: readonly ArrivalSource[], now: Date, urlOf: (subdomain: string, slug: string) => string,
  words?: (name: string) => { title: string; body: string },
): ArrivalRegion[] {
  return raves
    .filter((r) => r.attending !== false && typeof r.lat === 'number' && typeof r.lng === 'number'
      && Number.isFinite(r.lat) && Number.isFinite(r.lng) && r.gatesOpenAt && r.endsAt
      && new Date(r.endsAt).getTime() > now.getTime()
      && new Date(r.gatesOpenAt).getTime() - ARRIVAL_LEAD_MS <= now.getTime())
    .sort((a, b) => a.gatesOpenAt!.localeCompare(b.gatesOpenAt!))
    .slice(0, MAX_ARRIVAL_REGIONS)
    .map((r) => ({
      id: r.editionId, name: r.editionName, lat: r.lat!, lng: r.lng!, radiusM: ARRIVAL_RADIUS_M,
      from: new Date(new Date(r.gatesOpenAt!).getTime() - ARRIVAL_LEAD_MS).toISOString(),
      to: new Date(r.endsAt!).toISOString(),
      url: urlOf(r.subdomain, r.editionSlug),
      ...(words ? words(r.editionName) : {}),
    }));
}

/** Entering a watched place: tell once per rave, and only inside its window. */
export function arrivalDecision(
  region: ArrivalRegion | undefined, now: Date, told: Readonly<Record<string, string>>,
): { notify: ArrivalRegion | null; told: Record<string, string> } {
  if (!region) return { notify: null, told: { ...told } };
  const t = now.getTime();
  if (t < new Date(region.from).getTime() || t > new Date(region.to).getTime()) return { notify: null, told: { ...told } };
  if (told[region.id]) return { notify: null, told: { ...told } };
  return { notify: region, told: { ...told, [region.id]: now.toISOString() } };
}

/** The words: the rave by name, and what the phone already holds. */
export function arrivalText(region: Pick<ArrivalRegion, 'name' | 'title' | 'body'>): { title: string; body: string } {
  if (region.title && region.body) return { title: region.title, body: region.body };
  return { title: `You're at ${region.name} 🎉`, body: 'Welcome! Your lineup and the map work with no signal — tap to open the rave.' };
}

/** A region from the page, checked: every field its shape, or null. */
export function parseArrivalRegion(v: unknown): ArrivalRegion | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const str = (x: unknown, max: number) => (typeof x === 'string' && x.length > 0 && x.length <= max ? x : null);
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : null);
  const id = str(o.id, 100); const name = str(o.name, 200); const url = str(o.url, 500);
  const from = str(o.from, 40); const to = str(o.to, 40);
  const lat = num(o.lat); const lng = num(o.lng); const radiusM = num(o.radiusM);
  if (!id || !name || !url || !from || !to || lat === null || lng === null || radiusM === null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || radiusM < 100 || radiusM > 10_000) return null;
  if (!/^https:\/\//.test(url) || Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(to))) return null;
  const title = str(o.title, 200); const body = str(o.body, 500);
  return { id, name, lat, lng, radiusM, from, to, url, ...(title && body ? { title, body } : {}) };
}
